/**
 * „Uložit jako poznatek" (spec 07 FR-1 → insight s provenancí `chat`).
 *
 * Confidence je povinná volba — poznatek bez přiznané míry jistoty je v téhle
 * znalostní bázi k ničemu (06 FR-2).
 */
import { useState } from 'react';
import { useCreateInsight } from '@/api/queries';
import type { Insight } from '@/api/models';

type Confidence = Insight['confidence'];

const CONFIDENCE_LABEL: Record<Confidence, string> = {
  confirmed: 'Potvrzeno — ověřeno na datech',
  probable: 'Pravděpodobné — indicie, ale ne důkaz',
  hypothesis: 'Hypotéza — k ověření',
};

export interface SaveInsightDialogProps {
  /** text odpovědi, ze které poznatek vzniká */
  content: string;
  onClose: () => void;
}

export function SaveInsightDialog({ content, onClose }: SaveInsightDialogProps) {
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [body, setBody] = useState(content);
  const [tags, setTags] = useState('');
  const [confidence, setConfidence] = useState<Confidence>('probable');
  const [error, setError] = useState<string | null>(null);
  const [duplicateOf, setDuplicateOf] = useState<string | null>(null);

  const create = useCreateInsight();

  const submit = async () => {
    if (!title.trim()) {
      setError('Poznatek potřebuje název — podle něj se hledá.');
      return;
    }
    setError(null);
    try {
      const saved = await create.mutateAsync({
        title: title.trim(),
        summary: summary.trim() || title.trim(),
        body_md: body,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
        confidence,
      });
      // Backend dedupuje — když našel podobný, řekne to místo tichého duplikátu.
      // Odpověď je `WriteResultOut` (created + duplicate_of + similar), ne insight.
      const duplicate = saved.duplicate_of ?? saved.similar?.[0]?.slug ?? null;
      if (!saved.created || duplicate) {
        setDuplicateOf(duplicate ?? saved.slug ?? '—');
        return;
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Uložení selhalo.');
    }
  };

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Uložit jako poznatek"
    >
      <div className="modal">
        <header className="modal__header">
          <h3>Uložit jako poznatek</h3>
          <button type="button" onClick={onClose} aria-label="Zavřít">
            ✕
          </button>
        </header>

        <div className="modal__body">
          <label className="field">
            <span>Název</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Např. Pilulka překonává X-countdown na všech webech"
              autoFocus
            />
          </label>

          <label className="field">
            <span>Shrnutí (jedna věta)</span>
            <input
              type="text"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Co si z toho odnést. Nevyplněné = použije se název."
            />
          </label>

          <label className="field">
            <span>Text</span>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} />
          </label>

          <label className="field">
            <span>Míra jistoty</span>
            <select
              value={confidence}
              onChange={(e) => setConfidence(e.target.value as Confidence)}
            >
              {(Object.keys(CONFIDENCE_LABEL) as Confidence[]).map((key) => (
                <option key={key} value={key}>
                  {CONFIDENCE_LABEL[key]}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Štítky (oddělené čárkou)</span>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="closer, ctr, dodavatel-a"
            />
          </label>

          <p className="modal__note">
            Uloží se s provenancí <code>chat</code>, takže bude zpětně vidět, že vznikl
            z AI konverzace. Pokud už podobný poznatek existuje, dostaneš návrh na jeho
            aktualizaci místo duplikátu.
          </p>

          {duplicateOf ? (
            <p className="modal__error" role="alert">
              Podobný poznatek už existuje: <code>{duplicateOf}</code>. Zvaž jeho
              aktualizaci místo založení nového.
            </p>
          ) : null}

          {error ? (
            <p className="modal__error" role="alert">
              {error}
            </p>
          ) : null}
        </div>

        <footer className="modal__footer">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onClose}
            disabled={create.isPending}
          >
            Zrušit
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={submit}
            disabled={create.isPending}
          >
            {create.isPending ? 'Ukládám…' : 'Uložit poznatek'}
          </button>
        </footer>
      </div>
    </div>
  );
}
